import React, { useMemo, useCallback, useState, useContext, useEffect } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import icons from 'icons'

import routerSelectors from 'store/selectors/router'
import bookingSelectors from 'store/selectors/booking'
import bookingActions from 'store/modules/booking'
import routerActions from 'store/modules/router'

import TimeRemaining from 'components/book/TimeRemaining'
import FormContext from './FormContext'
import Window from 'components/dialog/Window'
import SlotSummary from 'components/book/SlotSummary'
import TotalSummary from 'components/book/TotalSummary'

import Errors from 'components/form/Errors'
import FloatingSidebar from 'components/widgets/FloatingSidebar'

import utils from 'shared/src/utils'

const ArrowBackIcon = icons.arrowBack
const ArrowForwardIcon = icons.arrowForward
const HomeIcon = icons.home

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  footerButtons: {
    marginTop: theme.spacing(2),
  },
}))

const useButtonStyles = makeStyles(theme => ({
  button: {
    padding: 0,
    //marginBottom: theme.spacing(2),
  },
  buttons: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  backButton: {
    flexGrow: 0,
    textAlign: 'left',
  },
  homeButton: {
    flexGrow: 1,
    textAlign: 'center',
  },
  nextButton: {
    flexGrow: 0,
    textAlign: 'right',
  },
  errors: {
    width: '100%',
    marginTop: '0px',
    marginBottom: theme.spacing(2),
    '& p, li': {
      fontSize: '0.9em',
    },
    '& ul': {
      margin: '0px',
    }
    
  }
}))

const FormPageButtons = ({
  errors,
  showErrors,
  withBack = true,
  withForward = true,
  onBack,
  onSubmit,
  onHome,
}) => {
  const classes = useButtonStyles()
  const formContext = useContext(FormContext)

  const submitHandler = (direction) => {
    formContext.setDirection(direction)
    onSubmit()
  }

  return (
    <>
      <div className={ classes.buttons }>
        {
          withBack && (
            <div className={ classes.backButton }>
              <Button
                className={ classes.button }
                color="primary"
                onClick={ onBack }
              >
                <ArrowBackIcon />&nbsp;Back
              </Button>
            </div>
          )
        }
        <div className={ classes.homeButton }>
          <Button
            className={ classes.button }
            color="primary"
            onClick={ onHome }
          >
            <HomeIcon />&nbsp;Home
          </Button>
        </div>
        {
          withForward && (
            <div className={ classes.nextButton }>
              <Button
                className={ classes.button }
                color="primary"
                onClick={ () => submitHandler('forward') }
              >
                Next&nbsp;<ArrowForwardIcon />
              </Button>
            </div>
          )
        }
      </div>
      {
        showErrors && (
          <Errors
            errors={ errors }
            className={ classes.errors }
          />
        )
      }
    </>
  )
}

const SummaryWrapper = ({
  values = {},
  errors = {},
  showErrors,
  withStoreOptions,
  withBack,
  withForward,
  withAlert,
  children,
  onBack,
  onSubmit,
}) => {

  const [ isCancelling, setIsCancelling ] = useState(false)

  const classes = useStyles()
  const dispatch = useDispatch()
  const store = useStore()

  const {
    project,
    bookingform,
  } = useSelector(routerSelectors.params)
  const reservation = useSelector(bookingSelectors.reservation)
  const reservationPaymentStatus = useSelector(bookingSelectors.reservationPaymentStatus)
  const slot = useSelector(bookingSelectors.slot)
  const storeOptions = useSelector(bookingSelectors.options)
  const useOptions = withStoreOptions ? storeOptions : values
  const totalsSelector = useMemo(bookingSelectors.optionsTotals, [])
  const totals = totalsSelector(store.getState(), {
    options: useOptions,
  })
  const formConfig = useSelector(bookingSelectors.slotFormConfig)

  const depositTitle = useMemo(() => {
    return utils.getDeepValue(formConfig, 'payment.deposit_title', 'Pay Now')
  }, [
    formConfig,
  ])

  const timeoutReservation = useCallback(() => {
    dispatch(bookingActions.setReservationError('timeout'))
  }, [])

  const onCancelBooking = useCallback(async () => {
    if(!slot || !project || !bookingform) return
    await dispatch(bookingActions.cancelBooking({
      project,
      bookingform,
      slot,
    }))
    dispatch(routerActions.navigateTo('book.form.start', {
      project,
      bookingform,
    }))
  }, [
    project,
    bookingform,
    slot,
  ])

  useEffect(() => {
    if(formConfig) return
    dispatch(bookingActions.loadCalendarConfig({
      project,
      bookingform,
    }))
  }, [])

  if(!formConfig) return null

  return (
    <FloatingSidebar
      withAlert={ withAlert }
      sidebar={
        <>
          <FormPageButtons
            errors={ errors }
            showErrors={ showErrors }
            withBack={ withBack }
            withForward={ withForward }
            onBack={ onBack }
            onSubmit={ onSubmit }
            onHome={ () => setIsCancelling(true) }
          />
          <Divider className={ classes.divider } />
          <SlotSummary
            slot={ slot }
            options={ useOptions }
            booking={ reservation }
            formConfig={ formConfig }
            extraRows={
              reservation ? 
                [{
                  title: 'Booking Ref',
                  value: reservation.reference,
                }] : 
                []
            }
          />
          <Divider className={ classes.divider } />
          <TotalSummary
            rows={ totals.rows }
            formConfig={ formConfig }
            alternate
          />
          <Divider className={ classes.divider } />
          <TotalSummary
            total={ totals.total }
            title="Total"
            formConfig={ formConfig }
          />
          <TotalSummary
            total={ totals.paynow }
            title={ depositTitle }
            formConfig={ formConfig }
          />
          {
            reservationPaymentStatus == 'none' && (
              <>
                <Divider className={ classes.divider } />
                <TimeRemaining
                  reservation={ reservation }
                  onTimeout={ timeoutReservation }
                />
              </>
            )
          }
          {
            slot && project && bookingform && reservationPaymentStatus == 'none' && (
              <>
                <Divider className={ classes.divider } />
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={ () => setIsCancelling(true) }
                >
                  Cancel Booking
                </Button>
              </>
            )
          }
          <Divider className={ classes.divider } />
          <FormPageButtons
            errors={ errors }
            showErrors={ showErrors }
            withBack={ withBack }
            withForward={ withForward }
            onBack={ onBack }
            onSubmit={ onSubmit }
            onHome={ () => setIsCancelling(true) }
          />
        </>
      }
    >
      { children }
      <div className={ classes.footerButtons }>
        <FormPageButtons
          errors={ errors }
          showErrors={ showErrors }
          withBack={ withBack }
          withForward={ withForward }
          onBack={ onBack }
          onSubmit={ onSubmit }
          onHome={ () => setIsCancelling(true) }
        />
      </div>
      {
        isCancelling && (
          <Window
            open
            size="sm"
            title="Cancel Booking?"
            withCancel
            cancelTitle="No - Keep Booking"
            submitTitle="Yes - Cancel Booking"
            onCancel={ () => setIsCancelling(false) }
            onSubmit={ onCancelBooking }
          >
            Are you sure you want to cancel this booking?
          </Window>
        )
      }
    </FloatingSidebar>
  )
}

export default SummaryWrapper